import { computed, ref, watch } from 'vue'
import { useGetAccountQuery, Module } from '~/types/types'
import { copyTextToClipboard } from '~/functions/helpers'

export const PORTFOLIOS_WITHOUT_DEFAULT_COMPANY_INFO = new Set([
  '49f75be7-fd52-4dbb-9a6f-d3c248a87dfd',
  'aac2227c-a950-4a16-88cd-c4f6559dff72',
])

export function usePortfolioInviteModules() {
  const { result, refetch: refetchAccount } = useGetAccountQuery()
  const account = computed(() => result.value?.getAccount)

  const addEsgToken = ref(false)
  const addImpactGoalsToken = ref(false)
  const addImpactStrategyToken = ref(false)
  const addMetricsToken = ref(false)
  const addCompanyInfo = ref(true)

  watch(
    account,
    (acc) => {
      if (acc?.modules && Array.isArray(acc.modules)) {
        addEsgToken.value = acc.modules.includes(Module.Esg)
        addImpactGoalsToken.value = acc.modules.includes(Module.ImpactGoal)
        addImpactStrategyToken.value =
          acc.modules.includes(Module.ImpactStrategy) &&
          acc.modules.includes(Module.ImpactGoal)
        addMetricsToken.value = acc.modules.includes(Module.Metrics)
      }
      if (acc?.id) {
        addCompanyInfo.value = !PORTFOLIOS_WITHOUT_DEFAULT_COMPANY_INFO.has(
          String(acc.id),
        )
      }
    },
    { immediate: true },
  )

  watch(addImpactGoalsToken, (isImpactGoalEnabled) => {
    if (!isImpactGoalEnabled) {
      addImpactStrategyToken.value = false
    }
  })

  const checkIsDisabled = (module: Module): boolean => {
    if (module === Module.CompanyInfo) {
      return false
    }
    if (!account?.value?.modules || !Array.isArray(account.value.modules)) {
      return true
    }
    return !account.value.modules.includes(module)
  }

  const hasAtLeastOneModule = computed(() => {
    if (!account.value?.modules) return false
    return (
      (!checkIsDisabled(Module.Esg) && addEsgToken.value) ||
      (!checkIsDisabled(Module.ImpactGoal) && addImpactGoalsToken.value) ||
      (!checkIsDisabled(Module.Metrics) && addMetricsToken.value)
    )
  })

  const selectedModules = computed<Module[]>(() => {
    const modules: Module[] = []
    if (addEsgToken.value && !checkIsDisabled(Module.Esg)) {
      modules.push(Module.Esg)
    }
    if (addImpactGoalsToken.value && !checkIsDisabled(Module.ImpactGoal)) {
      modules.push(Module.ImpactGoal)
    }
    if (
      addImpactStrategyToken.value &&
      !checkIsDisabled(Module.ImpactStrategy) &&
      addImpactGoalsToken.value
    ) {
      modules.push(Module.ImpactStrategy)
    }
    if (addMetricsToken.value && !checkIsDisabled(Module.Metrics)) {
      modules.push(Module.Metrics)
    }
    if (addCompanyInfo.value) {
      modules.push(Module.CompanyInfo)
    }
    return modules
  })

  const shareLink = computed(() => {
    if (!account?.value) {
      return ''
    }
    const isImpactStrategyEnabled =
      addImpactGoalsToken.value &&
      addImpactStrategyToken.value &&
      !checkIsDisabled(Module.ImpactStrategy)
    let link =
      window.location.host + `/register/?portfolio=${account.value.id}`
    if (addMetricsToken.value && !checkIsDisabled(Module.Metrics)) {
      link = `${link}&metricsToken=${account.value.metricsToken}`
    }
    if (addEsgToken.value && !checkIsDisabled(Module.Esg)) {
      link = `${link}&esgToken=${account.value.esgToken}`
    }
    if (addImpactGoalsToken.value && !checkIsDisabled(Module.ImpactGoal)) {
      link = `${link}&impactGoalToken=${account.value.impactGoalToken}`
    }
    link = `${link}&impactStrategy=${isImpactStrategyEnabled}`
    link = `${link}&companyInfo=${addCompanyInfo.value}`
    return link
  })

  const copied = ref(false)

  async function copyShareLink(
    onError: (message: string) => void,
  ): Promise<void> {
    const url = `${window.location.protocol}//${shareLink.value}`
    const ok = await copyTextToClipboard(url)
    if (!ok) {
      onError('Failed to copy link to clipboard.')
      return
    }
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  }

  return {
    account,
    refetchAccount,
    Module,
    addEsgToken,
    addImpactGoalsToken,
    addImpactStrategyToken,
    addMetricsToken,
    addCompanyInfo,
    checkIsDisabled,
    hasAtLeastOneModule,
    selectedModules,
    shareLink,
    copied,
    copyShareLink,
  }
}
